import { IFieldConfig } from "../../../../../components/Form/interface/form.interfaces";


// ─── Static option lists ───────────────────────────────────────────────────────
//
// These three lists are intentionally kept in sync by index so that when you
// add country-linking logic (e.g. a useEffect / onChange handler that sets
// countryCode and dialCode automatically when country changes), you can derive
// the sibling values by finding the matching entry here.
//
// The arrays are sorted alphabetically by country name for UX consistency.

export interface CountryEntry {
    name: string;
    code: string; // ISO 3166-1 alpha-2
    dial: string; // E.164 prefix without leading +
}

export function getDialSelectValue(entry: CountryEntry): string {
    return `${entry.code}:${entry.dial}`;
}

export function getDialFromSelectValue(value: string): string {
    return value.includes(":") ? value.split(":").slice(1).join(":") : value;
}

export const COUNTRIES: CountryEntry[] = [
    { name: "Argentina", code: "AR", dial: "+54" },
    { name: "Australia", code: "AU", dial: "+61" },
    { name: "Brazil", code: "BR", dial: "+55" },
    { name: "Canada", code: "CA", dial: "+1" },
    { name: "Chile", code: "CL", dial: "+56" },
    { name: "China", code: "CN", dial: "+86" },
    { name: "Colombia", code: "CO", dial: "+57" },
    { name: "Costa Rica", code: "CR", dial: "+506" },
    { name: "Ecuador", code: "EC", dial: "+593" },
    { name: "Egypt", code: "EG", dial: "+20" },
    { name: "France", code: "FR", dial: "+33" },
    { name: "Germany", code: "DE", dial: "+49" },
    { name: "Guatemala", code: "GT", dial: "+502" },
    { name: "India", code: "IN", dial: "+91" },
    { name: "Indonesia", code: "ID", dial: "+62" },
    { name: "Italy", code: "IT", dial: "+39" },
    { name: "Japan", code: "JP", dial: "+81" },
    { name: "Malaysia", code: "MY", dial: "+60" },
    { name: "Mexico", code: "MX", dial: "+52" },
    { name: "Netherlands", code: "NL", dial: "+31" },
    { name: "New Zealand", code: "NZ", dial: "+64" },
    { name: "Nigeria", code: "NG", dial: "+234" },
    { name: "Panama", code: "PA", dial: "+507" },
    { name: "Peru", code: "PE", dial: "+51" },
    { name: "Philippines", code: "PH", dial: "+63" },
    { name: "Poland", code: "PL", dial: "+48" },
    { name: "Portugal", code: "PT", dial: "+351" },
    { name: "Russia", code: "RU", dial: "+7" },
    { name: "Saudi Arabia", code: "SA", dial: "+966" },
    { name: "Singapore", code: "SG", dial: "+65" },
    { name: "South Africa", code: "ZA", dial: "+27" },
    { name: "South Korea", code: "KR", dial: "+82" },
    { name: "Spain", code: "ES", dial: "+34" },
    { name: "Sweden", code: "SE", dial: "+46" },
    { name: "Switzerland", code: "CH", dial: "+41" },
    { name: "Turkey", code: "TR", dial: "+90" },
    { name: "United Kingdom", code: "GB", dial: "+44" },
    { name: "United States", code: "US", dial: "+1" },
    { name: "Uruguay", code: "UY", dial: "+598" },
    { name: "Venezuela", code: "VE", dial: "+58" },
];

// Derived option lists — generated from the single COUNTRIES source of truth
// so the three selects always stay consistent.

const COUNTRY_OPTIONS = [
    { label: "Select country", value: "" },
    ...COUNTRIES.map((c) => ({ label: c.name, value: c.name })),
];

const COUNTRY_CODE_OPTIONS = [
    { label: "Select code", value: "" },
    ...COUNTRIES.map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code })),
];

const DIAL_CODE_OPTIONS = [
    { label: "Select dial code", value: "" },
    ...COUNTRIES.map((c) => ({
        label: `${c.dial} (${c.code})`,
        value: getDialSelectValue(c),
    })),
];

// ─── Cross-field helpers ───────────────────────────────────────────────────────
//
// If you want to auto-populate countryCode and dialCode when country changes,
// wire a handler at the feature level:
//
//   const handleCountryChange = (country: string) => {
//     const entry = COUNTRIES.find((c) => c.name === country);
//     if (entry) {
//       form.setValue("countryCode", entry.code);
//       form.setValue("dialCode", entry.dial);
//     }
//   };
//
// Until then, the custom validators below flag mismatches so the user at least
// cannot submit an inconsistent combination.

function getEntryByCountry(country: string): CountryEntry | undefined {
    return COUNTRIES.find((c) => c.name === country);
}

// ─── Field config ──────────────────────────────────────────────────────────────

export const signupFields: IFieldConfig[] = [
    // ── Personal ────────────────────────────────────────────────────────────────
    {
        name: "firstName",
        label: "First name",
        type: "text",
        placeholder: "Jane",
        required: true,
        cols: 1,
        validation: {
            minLength: { value: 2, message: "First name must be at least 2 characters" },
            maxLength: { value: 50, message: "First name must be at most 50 characters" },
            pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: "First name contains invalid characters",
            },
        },
    },
    {
        name: "lastName",
        label: "Last name",
        type: "text",
        placeholder: "Doe",
        required: true,
        cols: 1,
        validation: {
            minLength: { value: 2, message: "Last name must be at least 2 characters" },
            maxLength: { value: 50, message: "Last name must be at most 50 characters" },
            pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: "Last name contains invalid characters",
            },
        },
    },
    {
        name: "alias",
        label: "Alias",
        type: "text",
        placeholder: "How people should see you",
        cols: 2,
        validation: {
            maxLength: { value: 80, message: "Alias must be at most 80 characters" },
        },
    },

    // ── Credentials ─────────────────────────────────────────────────────────────
    {
        name: "email",
        label: "Email address",
        type: "email",
        placeholder: "jane.doe@example.com",
        required: true,
        cols: 2,
        validation: {
            pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
            },
        },
    },
    {
        name: "password",
        label: "Password",
        type: "password",
        placeholder: "••••••••",
        required: true,
        cols: 1,
        helperText: "At least 8 characters. Use a mix of letters, numbers, and symbols.",
        validation: {
            minLength: { value: 8, message: "Password must be at least 8 characters" },
            maxLength: { value: 128, message: "Password must be at most 128 characters" },
            pattern: {
                value: /^(?=.*[a-zA-Z])(?=.*\d).+$/,
                message: "Password must include at least one letter and one number",
            },
        },
    },
    {
        name: "passwordConfirmation",
        label: "Confirm password",
        type: "password",
        placeholder: "••••••••",
        required: true,
        cols: 1,
        validation: {
            custom: (value, all) => {
                if (value !== all.password) return "Passwords do not match";
            },
        },
    },

    // ── Location anchor ──────────────────────────────────────────────────────────
    //
    // country is intentionally placed before countryCode and dialCode so that
    // cross-field validators on those two fields always have a resolved country
    // value to compare against. Rendering it after would let users fill the
    // derived fields first and receive premature mismatch errors.
    {
        name: "country",
        label: "Country",
        type: "select",
        required: true,
        cols: 2,
        options: COUNTRY_OPTIONS,
    },

    // ── Phone ────────────────────────────────────────────────────────────────────
    //
    // countryCode, dialCode and phone are grouped together so the entire
    // "contact number" unit is co-located after the country anchor.
    {
        name: "countryCode",
        label: "Country code (ISO 3166-1 alpha-2)",
        type: "select",
        required: true,
        cols: 1,
        options: COUNTRY_CODE_OPTIONS,
        helperText: "Should match the country selected above",
        validation: {
            custom: (value, all) => {
                if (!all.country || !value) return null;
                const entry = getEntryByCountry(all.country as string);
                if (entry && entry.code !== value) {
                    return `Country code does not match the selected country (expected ${entry.code})`;
                }
                return null;
            },
        },
    },
    {
        name: "dialCode",
        label: "Dial code",
        type: "select",
        required: true,
        cols: 1,
        options: DIAL_CODE_OPTIONS,
        validation: {
            custom: (value, all) => {
                if (!all.country || !value) return null;
                const entry = getEntryByCountry(all.country as string);
                const dial = getDialFromSelectValue(value as string);
                const countryCode = (value as string).split(":")[0];
                if (entry && (entry.dial !== dial || entry.code !== countryCode)) {
                    return `Dial code does not match the selected country (expected ${entry.dial})`;
                }
                return null;
            },
        },
    },
    {
        name: "phone",
        label: "Phone number",
        type: "text",
        placeholder: "5551234567",
        required: true,
        cols: 2,
        helperText: "Digits only, without the country dial code",
        validation: {
            pattern: {
                value: /^\d{6,15}$/,
                message: "Enter between 6 and 15 digits, no spaces or dashes",
            },
        },
    },

    // ── Location detail ───────────────────────────────────────────────────────────
    {
        name: "state",
        label: "State / Province / Region",
        type: "text",
        placeholder: "California",
        required: true,
        cols: 1,
        validation: {
            maxLength: { value: 100, message: "State must be at most 100 characters" },
        },
    },
    {
        name: "city",
        label: "City",
        type: "text",
        placeholder: "San Francisco",
        required: true,
        cols: 1,
        validation: {
            maxLength: { value: 100, message: "City must be at most 100 characters" },
        },
    },
    {
        name: "zipCode",
        label: "ZIP / Postal code",
        type: "text",
        placeholder: "94102",
        required: true,
        cols: 1,
        validation: {
            pattern: {
                value: /^[a-zA-Z0-9\s-]{3,10}$/,
                message: "Enter a valid postal code (3–10 alphanumeric characters)",
            },
        },
    },
    {
        name: "mailingAddress",
        label: "Mailing address",
        type: "address",
        placeholder: "123 Main St, Apt 4B",
        required: true,
        cols: 1,
        helperText: "Street address, apartment, suite, unit, floor, etc.",
        validation: {
            minLength: { value: 5, message: "Address must be at least 5 characters" },
            maxLength: { value: 255, message: "Address must be at most 255 characters" },
        },
    },
];

// ─── Form-level config ────────────────────────────────────────────────────────
//
// onSubmit is intentionally omitted here — inject it at the feature layer
// where you have access to your API client, router, and auth store.
//
// Usage:
//   import { signupFormConfig, signupFields } from "./signupFormConfig";
//   import { DynamicForm } from "@/shared/ui/DynamicForm";
//
//   <DynamicForm
//     {...signupFormConfig}
//     onSubmit={handleSignup}
//   />

export const signupFormConfig = {
    fields: signupFields,
    columns: 2,
    submitLabel: "Create account",
}
